import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../components/ui/use-toast';
// Firebase imports  
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
//@ts-ignore  
import fullLogo from '../assets/logo/ByteSplash_Logo.png';
import { app } from "../firebase";

const Login = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

    const db = getFirestore(app);
    const auth = getAuth(app);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        // Simple validation  
        if (!email || !password) {
            toast({
                title: "Error",
                description: "Please fill all fields",
                variant: "destructive",
            });
            setIsLoading(false);
            return;
        }

        try {
            // Sign in with Firebase  
            const userCredential = await signInWithEmailAndPassword(auth, email, password);

            // Store user data in local storage  
            const user = userCredential.user;
            const accessToken = await user.getIdToken(); // Retrieve the access token  

            const userInfo = {
                emailId: user.email,
                accessToken,
                uid: user.uid,
                tokenCreatedAt: Date.now()
            };

            localStorage.setItem('user', JSON.stringify(userInfo));

            toast({
                title: "Login Successful",
                description: "Welcome back to ByteSplash Education Management System.",
            });

            // Set the user as authenticated  
            setIsAuthenticated(true);
           window.location.href= "/dashboard";
        } catch (error) {
            // Handle login error  
            console.error("Login Error:", error);
            toast({
                title: "Login failed",
                description: "Invalid email or password",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };




    // Redirect based on authentication state  
    useEffect(() => {
        const checkUserLoggedIn = () => {
            const userData = JSON.parse(localStorage.getItem('user')!) ?? null;

            // If a user is already authenticated  
            if (userData && userData.accessToken && userData.tokenCreatedAt) {
                setIsAuthenticated(true); // Set authenticated state  
                // if (isAuthenticated) {
                // Redirect to the respective location if authenticated  
                const currentPath = window.location.pathname;
                if (currentPath === '/' || currentPath === '' || currentPath === '/login') {
                    window.location.href= '/dashboard';
                } else {
                    navigate(currentPath);
                }
                //  }
            }
        }

        checkUserLoggedIn();
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center mb-8 text-[#0071bd]">
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Back to Home
                </Link>

                <Card className="w-full">
                    <CardHeader className="space-y-1 text-center">
                        <CardTitle className="text-2xl font-bold">
                            <img src={fullLogo} height={40} width={280} alt="Full Logo" />
                            <h1 className="text-lg text-gray-400 font-semibold mb-4 text-center">Registration Portal</h1>
                        </CardTitle>
                        <CardDescription>Enter your credentials to access your account</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs defaultValue="login" className="w-full">
                            <TabsList className="grid w-full grid-cols-2 mb-6">
                                <TabsTrigger value="login">Login</TabsTrigger>
                            </TabsList>

                            <TabsContent value="login">
                                <form onSubmit={handleLogin} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input id="email" type="email" placeholder="example@bytesplash.in" value={email} onChange={(e) => setEmail(e.target.value)} required />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor="password">Password</Label>
                                            <a href="#" className="text-sm text-blue-600 hover:text-blue-800">
                                                Forgot password?
                                            </a>
                                        </div>
                                        <Input id="password" type="password" placeholder='********' value={password} onChange={(e) => setPassword(e.target.value)} required />
                                    </div>
                                    <Button type="submit" className="w-full" disabled={isLoading}>
                                        {isLoading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Logging in...
                                            </>
                                        ) : "Login"}
                                    </Button>
                                </form>
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                    <CardFooter className="flex flex-col">
                        <p className="text-xs text-center text-gray-500 mt-4">
                            By clicking continue, you agree to our{' '}
                            <a href="#" className="text-blue-600 hover:text-blue-800">
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a href="#" className="text-blue-600 hover:text-blue-800">
                                Privacy Policy
                            </a>
                        </p>
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
};

export default Login;  